import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  use,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import useLazyLoading from "../../hooks/useLazyLoading";
import { fetchWithTimeout } from "../../hooks/useFetchWithTimeout";

// CSS
import "./itemView.css";
import "../../Pages/0PageStyles.css";

// Components
import Title from "../Other Components/Title";
import SearchBar from "../Other Components/searchBar";
import ItemCardHeader from "../Item Components/itemCardHeader";
import TagIcon from "../Tag Components/tagIcon";
import ItemCard from "../Item Components/itemCard";
import SortComponent from "../Other Components/sortComponent";
import Filter from "../Other Components/filter";
import {
  SelectionCategory,
  SliderCategory,
} from "../Other Components/Category";
import PageLoader from "../Other Components/pageLoader";
import Button from "../Other Components/button";
import BarcodeScannerComponent from "../Other Components/BarcodeScannerComponent";
import ExportData from "../Other Components/ExportDataComponent";

// Functions and hooks
import {
  GetItems,
  GetItemsInWarehouse,
  CheckForItem,
  GetItemAmountMinMax,
  GetItemPriceMinMax,
  GetItemsInNote,
  GetItemsPerWarehouse,
  GetItemsPerClient,
} from "../../BackendAccess/ItemHooks";
import { GetTagMap, GetTags } from "../../BackendAccess/TagHooks";
import { GetUnits } from "../../BackendAccess/UnitHooks";
import { useNotification } from "../../Contexts/NotificationContext";
import { useErrorHandler } from "../../hooks/errorHandler";
import useNarrowMode from "../../hooks/useNarrowModeChecker";
import { formatCurrency, useSettings } from "../../Contexts/SettingsContext";
import { useTranslation } from "react-i18next";
import { parseItemsCodeToMap } from "../../hooks/qrCodeCreator";

export default function ItemView({
  Tit = "Items",
  bEnableSort = true,
  bEnableFilter = true,
  bEnableSearch = true,
  bEnableCreate = true,
  bEnableEdit = true,
  bShowHeader = true,
  bShowTitle = true,
  bShowAll = false,
  bLazyLoading = true,
  btnSectionWidth = 1,
  bSelectionView = false,
  bCountable = false,
  bLimitAmount = false,
  updateTotalPrice,
  onselectedItemsChange,
  onSelectionItemAmountsChange,
  initialSelectionItemAmounts,
  onCardSelect,
  bNoteView,
  bEnableExport = true,
  noteType,
  propertiesToShow = {
    ID: { Show: true, Width: 2 },
    Barcode: { Show: true, Width: 2 },
    Name: { Show: true, Width: 3 },
    Unit: { Show: false, Width: 1 },
    Price: { Show: true, Width: 2 },
    Tags: { Show: true, Width: 4 },
    Clients: { Show: false, Width: 2 },
    Amount: { Show: true, Width: 2 },
  },
  setItemsOutsideView,
  WarehouseID = -1,
  ClientID = -1,
  NoteID = -1,
  bDisabled = false,
  disabledMsg = "Select a warehouse and client first.",
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [items, setItems] = useState([]);
  const showAllLocal = useMemo(
    () => bShowAll || (WarehouseID === -1 && NoteID === -1),
    [bShowAll, WarehouseID, NoteID]
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageError, setPageError] = useState(null);

  const { showNotification } = useNotification();
  useErrorHandler(error, showNotification, setPageError);

  const [categoryArray, setCategoryArray] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [units, setUnits] = useState([]);
  const [AmountLimits, setAmountLimits] = useState([]);
  const [PriceLimits, setPriceLimits] = useState([]);
  const [tagMap, setTagMap] = useState(new Map());
  const [tags, setTags] = useState([]);
  const [itemsPerWarehouseMap, setItemsPerWarehouseMap] = useState(new Map());
  const [itemsPerClientMap, setItemsPerClientMap] = useState(new Map());

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectionItemAmounts, setSelectionItemAmounts] = useState(new Map()); // Internal state
  const [totalPrice, setTotalPrice] = useState(0);
  const [itemsLoaded, setItemsLoaded] = useState(false);

  const isNarrow = useNarrowMode();

  const searchFilteredItems = useMemo(() => {
    if (!searchTerm) return filteredItems;

    const lowerCaseTerm = searchTerm.toLowerCase();
    return filteredItems.filter(
      (item) =>
        item.Name.toLowerCase().includes(lowerCaseTerm) ||
        item.ItemID.toString().toLowerCase().includes(lowerCaseTerm) ||
        (item.Barcode?.toString() || "").includes(lowerCaseTerm)
    );
  }, [searchTerm, filteredItems]);

  const lazyLoadItemLoadCount = 20;
  const lazyLoadPixelThreshold = 200;

  const { loadedItems, itemContainerRef } = useLazyLoading(
    searchFilteredItems,
    lazyLoadItemLoadCount,
    lazyLoadPixelThreshold
  );

  const { companySettings, refreshCompanySettings } = useSettings();

  const { t } = useTranslation();

  const getKeyByValue = useCallback((map, value) => {
    const entry = Array.from(map.entries()).find(([key, val]) => val === value);
    return entry ? entry[0] : null;
  }, []);
  //
  //
  //
  //
  //
  //
  //***********************************Fetching all data*********************************/

  const fetchItems = useCallback(async () => {
    await refreshCompanySettings();
    const fetchFunction = showAllLocal
      ? GetItems
      : WarehouseID !== -1
      ? () => GetItemsInWarehouse(WarehouseID)
      : () => GetItemsInNote(NoteID);

    const [data, error] = await fetchWithTimeout(fetchFunction);

    if (error) throw error;

    const filteredData =
      showAllLocal || ClientID === -1
        ? data
        : data.filter((item) =>
            item.Clients.split(",").includes(ClientID.toString())
          );

    setItems(filteredData);
    console.log(
      "TESTINGGGG calling loadAmountsAndIds with data",
      filteredData,
      ClientID
    );
    await loadAmountsAndIds(filteredData);
    if (setItemsOutsideView && typeof setItemsOutsideView === "function") {
      setItemsOutsideView(filteredData);
    }
    return filteredData;
  }, [showAllLocal, WarehouseID, NoteID, ClientID]);

  const fetchItemsPerWarehouse = useCallback(async () => {
    if (!showAllLocal) return;
    const [data, error] = await fetchWithTimeout(GetItemsPerWarehouse);
    if (error) throw error;
    console.log("categories WAREHOUSE DATA", data);
    setItemsPerWarehouseMap(data);
  }, [showAllLocal]);

  const fetchItemsPerClient = useCallback(async () => {
    if (!showAllLocal) return;
    const [data, error] = await fetchWithTimeout(GetItemsPerClient);
    if (error) throw error;
    setItemsPerClientMap(data);
  }, [showAllLocal]);

  const fetchTagData = useCallback(async () => {
    const [data, error] = await fetchWithTimeout(GetTags);
    if (error) throw error;
    setTags(data);
    console.log("Got tags", data);
  }, []);

  const fetchTags = useCallback(async () => {
    if (!tags?.length) return;
    const [data, error] = await fetchWithTimeout(() => GetTagMap(tags));
    if (error) throw error;
    setTagMap(data);
  }, [tags]);

  const fetchUnits = useCallback(async () => {
    const [data, error] = await fetchWithTimeout(GetUnits);
    if (error) throw error;
    setUnits(data.map((item) => item.Name));
  }, []);

  const fetchItemLimits = useCallback(async () => {
    const warehouseParam = showAllLocal ? -1 : WarehouseID;

    const [amountData, amountError] = await fetchWithTimeout(() =>
      GetItemAmountMinMax(warehouseParam)
    );
    if (!amountError) setAmountLimits(amountData);
    console.log("category ammounts", amountError, amountData);

    const [priceData, priceError] = await fetchWithTimeout(() =>
      GetItemPriceMinMax()
    );
    console.log("Test got price data", priceData);

    if (!priceError) setPriceLimits(priceData);

    if (priceError) throw priceError;
    if (amountError) throw amountError;
  }, [showAllLocal, WarehouseID]);

  const fetchDataFromServer = useCallback(async () => {
    setLoading(true);
    try {
      await fetchItems();
      await Promise.all([
        fetchItemsPerWarehouse(),
        fetchItemsPerClient(),
        fetchTagData(),
        fetchUnits(),
        fetchItemLimits(),
      ]);
    } catch (error) {
      error.isPageError = true;
      setError(error);
    } finally {
      setLoading(false);
    }
  }, [
    fetchItems,
    fetchItemsPerWarehouse,
    fetchItemsPerClient,
    fetchTagData,
    fetchUnits,
    fetchItemLimits,
  ]);

  useEffect(() => {
    fetchDataFromServer();
  }, [fetchDataFromServer, location.pathname, ClientID, WarehouseID, NoteID]);
  useEffect(() => {
    fetchDataFromServer();
  }, [location.pathname]);

  //***********************************Fetching all data END*********************************/
  //
  //
  //
  //
  //
  //*********************************Item amount selection *********************************/

  const loadAmountsAndIds = useCallback(
    async (data) => {
      if (bCountable) {
        const newSelectionItemAmounts = new Map();

        data.forEach((item) => {
          newSelectionItemAmounts.set(
            item.ItemID,
            selectionItemAmounts.get(item.ItemID) || 0
          );
        });
        console.log(
          "dsadsa dasdsa TESTINGGGG Loading amounts and IDs",
          ClientID,
          newSelectionItemAmounts
        );

        setSelectionItemAmounts(newSelectionItemAmounts);
      }

      setItemsLoaded(true);
    },
    [bCountable, selectionItemAmounts]
  );

  useEffect(() => {
    if (
      initialSelectionItemAmounts?.size > 0 &&
      selectionItemAmounts.size > 0 &&
      bDisabled
    ) {
      showNotification("Please select a warehouse and client first.", "error");
    } else if (selectionItemAmounts && initialSelectionItemAmounts?.size > 0) {
      createSelectionItemAmountsData(initialSelectionItemAmounts);
    }
  }, [initialSelectionItemAmounts]);

  const createSelectionItemAmountsData = (newMap) => {
    let newData = [];
    newData = Array.from(selectionItemAmounts).map(([key, _]) => ({
      ItemID: key,
      Amount: newMap?.get(key) ? newMap.get(key) : 0,
    }));
    loadAmountsAndIds(newData);
  };

  const changeItemSelectionAmount = useCallback(
    (itemID, amount, price, currentItemMaxAmount) => {
      // currentItemMaxAmount is item.Amount from ItemCard
      const newAmount =
        bLimitAmount &&
        currentItemMaxAmount !== null &&
        amount > currentItemMaxAmount
          ? currentItemMaxAmount
          : amount;

      setSelectionItemAmounts((prevMap) => {
        const newMap = new Map(prevMap);
        const oldAmount = prevMap.get(itemID) || 0;
        newMap.set(itemID, newAmount);

        // Recalculate total price based on the change
        const priceFloat = parseFloat(price);
        const newTotalPrice =
          totalPrice - oldAmount * priceFloat + newAmount * priceFloat;
        setTotalPrice(parseFloat(newTotalPrice.toFixed(2)));

        return newMap;
      });
    },
    [bLimitAmount, totalPrice] // totalPrice dependency is important for recalculation
  );

  useEffect(() => {
    if (isNaN(totalPrice)) {
      setTotalPrice(0);
    } else if (updateTotalPrice && typeof updateTotalPrice === "function") {
      updateTotalPrice(totalPrice);
    }
  }, [totalPrice, updateTotalPrice]);

  /*   const resetSelectionItemAmounts = useCallback(() => {
    console.log(
      "dsadsa dasdsa Resetting selection item amounts1111111111",
      selectionItemAmounts
    );

    const newMap = new Map(
      [...selectionItemAmounts.keys()].map((key) => [key, 0])
    );
    loadAmountsAndIds(newMap);

    console.log(
      "dsadsa dasdsa Resetting selection item amounts",
      selectionItemAmounts,
      newMap
    );
  }, [setSelectionItemAmounts]);

  useEffect(() => {
    console.log("dsadsa dasdsa ", noteType);
    if (selectionItemAmounts.size > 0) resetSelectionItemAmounts();
  }, [noteType]); */

  const recalculatePrice = useCallback(() => {
    let newTotalPrice = 0;
    selectionItemAmounts.forEach((amount, itemID) => {
      const item = items.find((item) => item.ItemID === itemID);
      console.log(
        "Recalculating price for item",
        itemID,
        amount,
        bCountable && ClientID === -1,
        item,
        parseFloat(
          bCountable && ClientID === -1 ? item.SellingPrice : item.Price
        ) || 0,
        amount *
          parseFloat(
            bCountable && ClientID === -1 ? item.SellingPrice : item.Price
          ) || 0
      );
      if (item) {
        const price =
          bCountable && ClientID === -1 ? item.SellingPrice : item.Price;
        newTotalPrice += amount * parseFloat(price) || 0;
      }
    });
    setTotalPrice(parseFloat(newTotalPrice.toFixed(2)));
  }, [selectionItemAmounts, items, bCountable, ClientID]);

  useEffect(() => {
    recalculatePrice();
  }, [noteType]);

  //*********************************Item amount selection END *********************************/
  //
  //
  //
  //
  //
  //
  //***********************************Filter*********************************/
  const ItemHasOptionTag = useCallback(
    (item, tag) => {
      return item.Tags.split(",").includes(getKeyByValue(tagMap, tag));
    },
    [tagMap, getKeyByValue]
  );

  const ItemHasOptionWarehouse = useCallback(
    (item, warehouse) => {
      return (
        itemsPerWarehouseMap.get(warehouse)?.includes(item.ItemID) || false
      );
    },
    [itemsPerWarehouseMap]
  );

  const ItemHasOptionClient = useCallback(
    (item, client) => {
      return itemsPerClientMap.get(client)?.includes(item.ItemID) || false;
    },
    [itemsPerClientMap]
  );

  const ItemHasOptionUnit = useCallback((item, unit) => {
    return item.Unit === unit;
  }, []);

  const ItemHasOptionAmount = useCallback((item, amountMin, amountMax) => {
    return item.Amount >= amountMin && item.Amount <= amountMax;
  }, []);

  const ItemHasOptionPrice = useCallback((item, priceMin, priceMax) => {
    console.log(
      "Test",
      item.Price,
      priceMin,
      priceMax,
      typeof priceMax,
      typeof priceMin
    );

    return item.Price >= priceMin && item.Price <= priceMax;
  }, []);

  const ItemIsSelected = useCallback(
    (item, selection) => {
      if (selection === t("Show only selected", { ns: "items" })) {
        return selectedItems.includes(item.ItemID);
      } else if (
        selection ===
        t("Show only with selected amount greater than 0", { ns: "items" })
      ) {
        return (selectionItemAmounts.get(item.ItemID) || 0) > 0;
      }
      return false;
    },
    [selectedItems, selectionItemAmounts]
  );

  const createCategoryArray = useCallback(() => {
    console.log("Setting categories array start");

    const showSelectedQuickBtn = bSelectionView;
    const showSelectedAmountQuickBtn = bCountable;

    const newCatArr = [
      new SelectionCategory(
        "Tags",
        Array.from(tagMap.keys()).map((item) => ({
          ItemName: tagMap.get(item),
          selected: false,
        })),
        false,
        ItemHasOptionTag
      ),
      new SelectionCategory(
        "Units",
        units.map((item) => ({ ItemName: item, selected: false })),
        false,
        ItemHasOptionUnit
      ),
      ...(showAllLocal && itemsPerWarehouseMap.size > 0
        ? [
            new SelectionCategory(
              "Warehouses",
              Array.from(itemsPerWarehouseMap.keys()).map((item) => ({
                ItemName: item,
                selected: false,
              })),
              true,
              ItemHasOptionWarehouse
            ),
          ]
        : []),
      ...(showAllLocal && itemsPerClientMap.size > 0
        ? [
            new SelectionCategory(
              "Clients",
              Array.from(itemsPerClientMap.keys()).map((item) => ({
                ItemName: item,
                selected: false,
              })),
              true,
              ItemHasOptionClient
            ),
          ]
        : []),
      new SelectionCategory(
        "Items to show",
        [
          {
            ItemName: t("Show only selected", { ns: "items" }),
            selected: false,
            quickButton: showSelectedQuickBtn,
          },
          {
            ItemName: t("Show only with selected amount greater than 0", {
              ns: "items",
            }),
            selected: false,
            quickButton: showSelectedAmountQuickBtn,
          },
        ],
        true,
        ItemIsSelected
      ),
      ...(PriceLimits
        ? [
            new SliderCategory(
              "Price",
              PriceLimits?.min || 0,
              PriceLimits?.max || 0,
              2,
              false,
              ItemHasOptionPrice
            ),
          ]
        : []),
      ...(AmountLimits
        ? [
            new SliderCategory(
              "Amount",
              Number(AmountLimits?.min || 0),
              Number(AmountLimits?.max || 0),
              0,
              false,
              ItemHasOptionAmount
            ),
          ]
        : []),
    ];
    console.log("Setting categories array", newCatArr);
    setCategoryArray(newCatArr);
  }, [
    t,
    tagMap,
    units,
    AmountLimits,
    PriceLimits,
    showAllLocal,
    itemsPerWarehouseMap,
    itemsPerClientMap,
    bSelectionView,
    bCountable,
    ItemHasOptionTag,
    ItemHasOptionWarehouse,
    ItemHasOptionClient,
    ItemHasOptionUnit,
    ItemHasOptionAmount,
    ItemHasOptionPrice,
    ItemIsSelected,
  ]);

  //***********************************Filter END*********************************/
  //
  //
  //
  //
  //
  //
  const sortFunctions = useMemo(
    () => ({
      Name: (direction) => {
        const itemsSorted = [...filteredItems].sort((a, b) =>
          a.Name.toUpperCase() > b.Name.toUpperCase()
            ? direction
            : -1 * direction
        );
        setItems(itemsSorted);
      },
      ID: (direction) => {
        const itemsSorted = [...filteredItems].sort((a, b) =>
          a.ItemID > b.ItemID ? direction : -1 * direction
        );
        setItems(itemsSorted);
      },
      Amount: (direction) => {
        const itemsSorted = [...filteredItems].sort(
          (a, b) => (a.Amount - b.Amount) * direction
        );
        setItems(itemsSorted);
      },
      Price: (direction) => {
        const itemsSorted = [...filteredItems].sort(
          (a, b) => (a.Price - b.Price) * direction
        );
        setItems(itemsSorted);
      },
    }),
    [filteredItems]
  );

  const handleSearchChange = useCallback((term) => {
    setSearchTerm(term);
  }, []);

  const handleEditClick = useCallback(
    async (id) => {
      const bItemExists = await CheckForItem(id);
      if (bItemExists) {
        navigate("/workspace/items/itemEditPage", { state: { id } });
      }
    },
    [navigate]
  );

  const handleCreateClick = useCallback(() => {
    navigate("/workspace/items/itemCreationPage");
  }, [navigate]);

  const setIDSelected = useCallback((id, state) => {
    setSelectedItems((prev) => {
      if (state) {
        return [...prev, id];
      } else {
        return prev.filter((item) => item !== id);
      }
    });
  }, []);

  const createHeaderCardProps = useMemo(() => {
    const cardPropArray = [
      { Value: "ID", columnWidth: propertiesToShow.ID.Width },
      { Value: "Name", columnWidth: propertiesToShow.Name.Width },
      ...(isNarrow
        ? []
        : [
            {
              Value: "Barcode",
              columnWidth: propertiesToShow.Barcode.Width,
            },
          ]),
      { Value: "Price", columnWidth: propertiesToShow.Price.Width },
      ...(isNarrow
        ? []
        : [{ Value: "Tags", columnWidth: propertiesToShow.Tags.Width }]),
      ...(isNarrow
        ? []
        : [
            {
              Value: "Clients",
              columnWidth: propertiesToShow.Clients.Width,
            },
          ]),
      { Value: "Amount", columnWidth: propertiesToShow.Amount.Width },
    ];

    return cardPropArray.filter((item) => propertiesToShow[item.Value].Show);
  }, [propertiesToShow, isNarrow, t]);

  const createCardProps = useCallback(
    (item) => {
      console.log(
        "dfsadfsadsada",
        noteType,
        noteType === "Received goods" ? item.Price : item.SellingPrice
      );
      const cardPropArray = [
        {
          Name: "ID",
          Value: item.ItemID,
          columnWidth: propertiesToShow.ID.Width,
        },
        {
          Name: "Name",
          Value: item.Name,
          columnWidth: propertiesToShow.Name.Width,
        },
        ...(isNarrow
          ? []
          : [
              {
                Name: "Barcode",
                Value: item.Barcode || "/",
                columnWidth: propertiesToShow.Barcode.Width,
              },
            ]),
        {
          Name: "Price",
          Value: noteType === "Received goods" ? item.Price : item.SellingPrice,
          columnWidth: propertiesToShow.Price.Width,
          Label: formatCurrency(
            noteType === "Received goods" ? item.Price : item.SellingPrice,
            companySettings.currency
          ),
        },
        ...(isNarrow
          ? []
          : [
              {
                Name: "Tags",
                Value:
                  item.Tags !== ""
                    ? item.Tags.split(",").map((tagId) => (
                        <TagIcon
                          key={tagId}
                          selectable={false}
                          tagID={tagId}
                          tagList={tags}
                        />
                      ))
                    : "",
                columnWidth: propertiesToShow.Tags.Width,
              },
            ]),
        ...(isNarrow
          ? []
          : [
              {
                Name: "Clients",
                Value: item.DateTime,
                columnWidth: propertiesToShow.Clients.Width,
              },
            ]),
        {
          Name: "Amount",
          Value: item.Amount,
          columnWidth: propertiesToShow.Amount.Width,
        },
      ];

      return cardPropArray.filter((item) => propertiesToShow[item.Name].Show);
    },
    [propertiesToShow, isNarrow, tags, bCountable, ClientID]
  );

  useEffect(() => {
    if (onselectedItemsChange && typeof onselectedItemsChange === "function") {
      onselectedItemsChange(selectedItems);
    }
  }, [selectedItems, onselectedItemsChange]);

  useEffect(() => {
    if (
      onSelectionItemAmountsChange &&
      typeof onSelectionItemAmountsChange === "function"
    ) {
      onSelectionItemAmountsChange(selectionItemAmounts);
    }
  }, [selectionItemAmounts, onSelectionItemAmountsChange]);

  useEffect(() => {
    if (tags?.length) {
      fetchTags();
    }
  }, [tags, fetchTags]);

  useEffect(() => {
    const requiredDataLoaded = tagMap.size > 0 && units.length > 0;
    if (requiredDataLoaded) {
      createCategoryArray();
    } else {
      console.log(
        "Data not loaded yet categories",
        tagMap.size > 0,
        units.length > 0,
        AmountLimits?.max,
        PriceLimits?.max,
        itemsPerWarehouseMap.size > 0 || showAllLocal,
        itemsPerClientMap.size > 0 || showAllLocal
      );
    }
  }, [
    tagMap,
    units,
    AmountLimits,
    PriceLimits,
    itemsPerWarehouseMap,
    itemsPerClientMap,
    showAllLocal,
    createCategoryArray,
  ]);

  useEffect(() => {
    if (items.length > 0) setFilteredItems(items);
  }, [items]);

  return (
    <PageLoader loading={loading} error={pageError}>
      <div className="item-view-container">
        {bDisabled && (
          <div className="itemView disabledCover">
            <p className="itemView disabledMsg">
              {t(disabledMsg, { ns: "items" })}
            </p>
          </div>
        )}

        {bShowTitle && <Title>{Tit}</Title>}

        {bEnableSearch && (
          <div className="search-bar-container">
            <SearchBar
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
              placeholder={t("Search items...", { ns: "items" })}
            />
          </div>
        )}

        <BarcodeScannerComponent
          className="w-90"
          onScanSuccess={handleSearchChange}
          enableShowCode={false}
        />

        <div className="sortAndFilterBar">
          {bEnableSort && (
            <SortComponent
              sortOpts={[t("ID"), t("Name"), t("Amount"), t("Price")]}
              sortOptFunctions={[
                sortFunctions.ID,
                sortFunctions.Name,
                sortFunctions.Amount,
                sortFunctions.Price,
              ]}
            />
          )}

          {bEnableFilter && (
            <Filter
              categoryArray={categoryArray}
              ConstItems={items}
              Items={filteredItems}
              setNewItems={setFilteredItems}
              setNewCatArr={setCategoryArray}
            />
          )}
        </div>

        <div className="item-card-and-header-container">
          {bShowHeader && (
            <ItemCardHeader
              propertiesToShow={createHeaderCardProps}
              btnText="Create"
              bBtnVisible={bEnableCreate}
              bBtnSectionVisible={bEnableCreate || bEnableEdit || bCountable}
              btnSectionWidth={btnSectionWidth}
              onClick={handleCreateClick}
            />
          )}

          {itemsLoaded && (
            <div className="item-card-container" ref={itemContainerRef}>
              {loadedItems.map((item) => (
                <ItemCard
                  key={item.ItemID}
                  bEditEnabled={bEnableEdit}
                  ItemID={item.ItemID}
                  propertiesToShow={createCardProps(item)}
                  bBtnVisible={bEnableEdit}
                  bBtnSectionVisible={
                    bEnableCreate || bEnableEdit || bCountable
                  }
                  btnSectionWidth={btnSectionWidth}
                  bBodyClickable={bSelectionView}
                  bSelectable={bSelectionView}
                  onClick={() => handleEditClick(item.ItemID)}
                  onSelected={onCardSelect}
                  selectedItemIds={selectedItems}
                  setIDSelected={setIDSelected}
                  btnText={t("Edit")}
                  bCountable={bCountable}
                  Amounts={selectionItemAmounts}
                  onChangeAmount={changeItemSelectionAmount}
                  bDisabled={loading}
                />
              ))}
            </div>
          )}

          {bEnableCreate && isNarrow && (
            <div className="item-view-narrow-btn-container">
              <Button
                btnStyle="btn-positive"
                icon="create"
                onClick={handleCreateClick}
              >
                {t("Create")}
              </Button>
            </div>
          )}
        </div>
        {bCountable && (
          <BarcodeScannerComponent
            className="w-90 mt-2"
            btnSize="btn-normal"
            scanBtnText="Scan Document Code"
            onScanSuccess={(code) => {
              const map = parseItemsCodeToMap(code);
              setSelectionItemAmounts(map);
            }}
            enableShowCode={false}
          />
        )}
        {bEnableExport && <ExportData data={searchFilteredItems} />}
      </div>
    </PageLoader>
  );
}
